import React, { useState, useEffect } from 'react';
import './GetQuote.css';
import { Heading } from '../../components';
import { FaBuilding, FaClock, FaComment, FaEnvelope, FaLeaf, FaMapMarkerAlt, FaPhoneAlt, FaRuler, FaTree, FaUser } from 'react-icons/fa';
import axios from "axios";

const API_PATH = 'https://botanica-react.asdesignsgalaxy.com/api/phpmailer/quote.php';
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const GetQuote = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    serviceType: '',
    address: '',
    propertyType: '',
    areaSize: '',
    gardenStyle: '',
    projectUrgency: '',
    comment: '',
    automatedIrrigation: false, // New field
    customPathways: false, // New field
    outdoorSeating: false, // New field
  });
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Client-side validation
      const validationErrors = [];
      
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.serviceType) validationErrors.push('Service type is required');
      if (!formData.address.trim()) validationErrors.push('Address is required');
      if (!formData.propertyType) validationErrors.push('Property type is required');
      if (!formData.areaSize) {
        validationErrors.push('Area size is required');
      } else if (isNaN(formData.areaSize) || formData.areaSize <= 0) {
        validationErrors.push('Area size must be a valid number');
      }
      if (!formData.gardenStyle) validationErrors.push('Garden style is required');
      if (!formData.projectUrgency) validationErrors.push('Project urgency is required');
      if (!formData.comment.trim()) validationErrors.push('Message is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // API call
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Quote request submission failed');
      }

      // Success handling
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        serviceType: '',
        address: '',
        propertyType: '',
        areaSize: '',
        gardenStyle: '',
        projectUrgency: '',
        comment: '',
        automatedIrrigation: false,
        customPathways: false,
        outdoorSeating: false,
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  return (
    <section className="get-quote linear-bg">
      <div className="box-container">
        <div className="content">
          <form className='dark-form' onSubmit={handleSubmitForm}>

            <Heading title={'Request A Quote'} subTitle={'Personalized Garden Solutions'} sideHeading linear />

            <div className='input-box'>
              <div className='input-field'>
                <label htmlFor="name"><FaUser /></label>
                <input type="text" className='box' id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="Name" disabled={isSubmitting} required />
              </div>

              <div className='input-field'>
                <label htmlFor="email"><FaEnvelope /></label>
                <input type="email" className='box' id="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Email" disabled={isSubmitting} required />
              </div>
            </div>

            <div className='input-box'>
              <div className='input-field'>
                <label htmlFor="number"><FaPhoneAlt /></label>
                <input type="tel" className='box' id="number" name="number" value={formData.number} onChange={handleInputChange} placeholder="Phone Number" disabled={isSubmitting} required />
              </div>

              <div className='select'>
                <div className="select-container">
                  <label htmlFor="serviceType"><FaLeaf /></label>
                  <select className='select-box' id="serviceType" name="serviceType" value={formData.serviceType} onChange={handleInputChange} disabled={isSubmitting} required >
                    <option value="">Service Type</option>
                    <option value="lawn-maintenance">Lawn Maintenance</option>
                    <option value="garden-design">Garden Design</option>
                    <option value="tree-planting">Tree Planting</option>
                    <option value="hardscaping">Hardscaping</option>
                    <option value="water-features">Water Features</option>
                    <option value="outdoor-lighting">Outdoor Lighting</option>
                  </select>
                </div>
              </div>
            </div>

            <div className='input-box'>
              <div className='input-field'>
                <label htmlFor="address"><FaMapMarkerAlt /></label>
                <input type="text" className='box' id="address" name="address" value={formData.address} onChange={handleInputChange} placeholder="Address" disabled={isSubmitting} required  />
              </div>
            </div>

            <div className='input-box'>
              <div className='select'>
                <div className="select-container">
                  <label htmlFor="propertyType"><FaBuilding /></label>
                  <select className='select-box' id="propertyType" name="propertyType" value={formData.propertyType} onChange={handleInputChange} disabled={isSubmitting} required >
                    <option value="">Property Type</option>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
              </div>

              <div className='input-field'>
                <label htmlFor="areaSize"><FaRuler /></label>
                <input type="number" className='box' id="areaSize" name="areaSize" min="0" value={formData.areaSize} onChange={handleInputChange} placeholder="Area Size (sq m)" disabled={isSubmitting} required  />
              </div>
            </div>

            <div className='input-box'>
              <div className='select'>
                <div className="select-container">
                  <label htmlFor="gardenStyle"><FaTree /></label>
                  <select className='select-box' id="gardenStyle" name="gardenStyle" value={formData.gardenStyle} onChange={handleInputChange} disabled={isSubmitting} required >
                    <option value="">Garden Style</option>
                    <option value="native">Native</option>
                    <option value="formal">Formal</option>
                    <option value="cottage">Cottage</option>
                    <option value="japanese">Japanese</option>
                    <option value="modern">Modern</option>
                    <option value="eclectic">Eclectic</option>
                    <option value="default">No Preference</option>
                  </select>
                </div>
              </div>

              <div className='select'>
                <div className="select-container">
                  <label htmlFor="projectUrgency"><FaClock /></label>
                  <select className='select-box' id="projectUrgency" name="projectUrgency" value={formData.projectUrgency} onChange={handleInputChange} disabled={isSubmitting} required >
                    <option value="">Project Urgency</option>
                    <option value="asap">ASAP</option>
                    <option value="within-a-month">Within a Month</option>
                    <option value="within-three-months">Within 1-3 Months</option>
                    <option value="no-rush">No Rush</option>
                  </select>
                </div>
              </div>
            </div>

            <div className='input-box'>
              <div className='input-field text-area'>
                <label htmlFor="comment"><FaComment /></label>
                <textarea cols="30" rows="10" id="comment" className='box' name="comment" value={formData.comment} onChange={handleInputChange} placeholder="Message" disabled={isSubmitting} required ></textarea>
              </div>
            </div>

            {/* Extra Services Checkboxes */}
            <div className="extra-services">
              <div className="checkbox-label">
                <input type="checkbox" id="automated-irrigation-checkbox" name="automatedIrrigation" checked={formData.automatedIrrigation} onChange={handleCheckboxChange} disabled={isSubmitting} />
                <label htmlFor="automated-irrigation-checkbox">Automated Irrigation</label>
                <div className="checkbox-indicator"></div>
              </div>

              <div className="checkbox-label">
                <input type="checkbox" id="custom-pathways-checkbox" name="customPathways" checked={formData.customPathways} onChange={handleCheckboxChange} disabled={isSubmitting} />
                <label htmlFor="custom-pathways-checkbox">Custom Pathways</label>
                <div className="checkbox-indicator"></div>
              </div>

              <div className="checkbox-label">
                <input type="checkbox" id="outdoor-seating-checkbox" name="outdoorSeating" checked={formData.outdoorSeating} onChange={handleCheckboxChange} disabled={isSubmitting} />
                <label htmlFor="outdoor-seating-checkbox">Outdoor Seating</label>
                <div className="checkbox-indicator"></div>
              </div>
            </div>

            <button type="submit" className='btn' disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Quote request submitted</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default GetQuote;